import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  PlayCircle,
  Repeat,
  TrendingDown,
  Wallet,
} from 'lucide-react';
import { PageHeader, ErrorState, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/misc';
import { formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useFinanceDashboard, useRunBilling } from './api';
import { AGEING_LABELS } from './labels';

/**
 * The finance dashboard from spec §11.5. Every figure is derived from the
 * underlying records, so what it shows can always be drilled into and checked.
 */
export function FinanceDashboardPage() {
  const { data, isLoading, error, refetch } = useFinanceDashboard();
  const runBilling = useRunBilling();

  if (isLoading) {
    return (
      <div>
        <PageHeader title="Finance" />
        <TableSkeleton rows={6} />
      </div>
    );
  }
  if (error || !data) {
    return <ErrorState message="Could not load the finance dashboard." onRetry={() => void refetch()} />;
  }

  const currency = data.baseCurrency;

  return (
    <div>
      <PageHeader
        title="Finance"
        description="Cash, what you are owed, what you owe, and what it is costing."
        actions={
          <Can permission="finance.invoice.create">
            <Button
              variant="outline"
              size="sm"
              disabled={runBilling.isPending}
              onClick={() => runBilling.mutate()}
              title="Generate subscription invoices, mark overdue and send reminders now"
            >
              <PlayCircle className="h-4 w-4" />
              {runBilling.isPending ? 'Running…' : 'Run billing'}
            </Button>
          </Can>
        }
      />

      {runBilling.data && (
        <div className="mb-5 rounded-md border bg-muted/40 px-4 py-2.5 text-sm">
          Billing run: {runBilling.data.subscriptions.generated} subscription invoices generated
          {runBilling.data.subscriptions.failed > 0 &&
            `, ${runBilling.data.subscriptions.failed} failed`}
          , {runBilling.data.overdue.marked} marked overdue, {runBilling.data.reminders.sent}{' '}
          reminders sent.
        </div>
      )}

      {data.unpricedCurrencies.length > 0 && (
        <div className="mb-5 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm">
          No exchange rate is held for {data.unpricedCurrencies.join(', ')}, so amounts in{' '}
          {data.unpricedCurrencies.length === 1 ? 'that currency are' : 'those currencies are'}{' '}
          counted here at face value. Fetch the rates under Finance settings → Currencies.
        </div>
      )}

      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={<Wallet className="h-4 w-4" />}
          label="Cash"
          value={formatMoney(data.cash.total, currency)}
          hint={`${data.cash.accounts.length} ${data.cash.accounts.length === 1 ? 'account' : 'accounts'}`}
          to="/finance/settings?tab=banks"
        />
        <Stat
          icon={<ArrowDownRight className="h-4 w-4" />}
          label="Owed to you"
          value={formatMoney(data.receivables.outstanding, currency)}
          hint={
            data.receivables.overdue && data.receivables.overdue !== '0.00'
              ? `${formatMoney(data.receivables.overdue, currency)} overdue`
              : 'Nothing overdue'
          }
          tone={
            data.receivables.overdue && data.receivables.overdue !== '0.00' ? 'warning' : undefined
          }
          to="/finance/reports?tab=ageing"
        />
        <Stat
          icon={<ArrowUpRight className="h-4 w-4" />}
          label="You owe"
          value={formatMoney(data.payables.total, currency)}
          hint={
            data.payables.dueThisWeek && data.payables.dueThisWeek !== '0.00'
              ? `${formatMoney(data.payables.dueThisWeek, currency)} due this week`
              : `${data.payables.count} unpaid`
          }
          to="/finance/expenses?unpaidOnly=true"
        />
        <Stat
          icon={<Banknote className="h-4 w-4" />}
          label="Working capital"
          value={formatMoney(data.workingCapital, currency)}
          hint="Cash + receivables − payables"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">This month</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5 text-sm">
            <Row label="Invoiced" value={formatMoney(data.revenue.invoicedThisMonth, currency)} />
            <Row label="Collected" value={formatMoney(data.revenue.collectedThisMonth, currency)} />
            <Row label="Expenses" value={formatMoney(data.expenses.thisMonth, currency)} />
            <Separator />
            <div className="flex items-center justify-between">
              <span className="font-medium">Net</span>
              <span
                className={cn(
                  'tabular font-semibold',
                  data.profitability.netThisMonth?.startsWith('-') && 'text-destructive',
                )}
              >
                {formatMoney(data.profitability.netThisMonth, currency)}
              </span>
            </div>
            {data.profitability.marginPercent !== null && (
              <div className="text-xs text-muted-foreground">
                {data.profitability.marginPercent}% margin
              </div>
            )}
            <Separator />
            <Row
              label="Invoiced this year"
              value={formatMoney(data.revenue.invoicedThisYear, currency)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Repeat className="h-4 w-4" />
              Recurring revenue
            </CardTitle>
            <CardDescription>From active subscriptions.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2.5 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-muted-foreground">MRR</span>
              <span className="tabular text-2xl font-semibold">
                {formatMoney(data.recurringRevenue.mrr, currency)}
              </span>
            </div>
            <Row label="ARR" value={formatMoney(data.recurringRevenue.arr, currency)} />
            <Row
              label="Active subscriptions"
              value={String(data.recurringRevenue.activeSubscriptions)}
            />
            {data.recurringRevenue.activeSubscriptions === 0 && (
              <p className="pt-1 text-xs text-muted-foreground">
                No subscriptions yet.{' '}
                <Link to="/finance/subscriptions" className="text-link hover:underline">
                  Start one
                </Link>
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Receivables ageing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {Object.entries(data.receivables.ageing).map(([bucket, amount]) => (
              <div key={bucket} className="flex items-center justify-between">
                <span
                  className={cn(
                    'text-muted-foreground',
                    bucket === '90+' && amount !== '0.00' && 'font-medium text-destructive',
                  )}
                >
                  {AGEING_LABELS[bucket] ?? bucket}
                </span>
                <span className="tabular">{formatMoney(amount, currency)}</span>
              </div>
            ))}
            <Separator />
            <Link
              to="/finance/reports?tab=ageing"
              className="block text-xs text-link hover:underline"
            >
              Full ageing report
            </Link>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Who owes the most</CardTitle>
          </CardHeader>
          <CardContent>
            {data.receivables.topDebtors.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Nothing outstanding.
              </p>
            ) : (
              <ul className="space-y-2">
                {data.receivables.topDebtors.map((debtor) => (
                  <li key={debtor.companyId} className="flex items-center justify-between text-sm">
                    <span className="truncate">{debtor.name}</span>
                    <span className="tabular font-medium">
                      {formatMoney(debtor.amount, currency)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Where money went</CardTitle>
            <CardDescription>Approved expenses this month.</CardDescription>
          </CardHeader>
          <CardContent>
            {data.expenses.byCategory.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Nothing recorded yet.
              </p>
            ) : (
              <ul className="space-y-2 text-sm">
                {data.expenses.byCategory.slice(0, 8).map((category) => (
                  <li
                    key={category.categoryId ?? category.name}
                    className="flex items-center justify-between"
                  >
                    <span className="truncate text-muted-foreground">{category.name}</span>
                    <span className="tabular">{formatMoney(category.amount, currency)}</span>
                  </li>
                ))}
              </ul>
            )}

            {data.runwayMonths !== null && (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <TrendingDown className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  At the current burn rate, cash lasts about{' '}
                  <strong>{data.runwayMonths} months</strong>.
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  hint,
  tone,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint: string;
  tone?: 'warning';
  to?: string;
}) {
  const body = (
    <Card className={cn('h-full', to && 'transition-shadow hover:shadow-md')}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className="mt-2 tabular text-2xl font-semibold">{value}</div>
        <div
          className={cn(
            'mt-1 flex items-center gap-1 text-xs',
            tone === 'warning' ? 'text-amber-600' : 'text-muted-foreground',
          )}
        >
          {tone === 'warning' && <AlertTriangle className="h-3 w-3" />}
          {hint}
        </div>
      </CardContent>
    </Card>
  );

  return to ? <Link to={to}>{body}</Link> : body;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}
